/**
 * StoragePanel - UI panel for interacting with storage structures (chests)
 */

import { UIPanel } from './UIPanel';
import { Vector2 } from '../math/Vector2';
import { InventoryComponent } from '../items/InventoryComponent';
import { ItemStack } from '../items/Item';
import { t, Localization } from '../systems/Localization';
import { renderItemIcon, hasItemIcon } from './ItemIcons';

export class StoragePanel extends UIPanel {
  private playerInventory: InventoryComponent | null = null;
  private storageInventory: InventoryComponent | null = null;

  private slotSize: number = 40;
  private slotsPerRow: number = 5;
  private hoveredSlot: { container: 'player' | 'storage'; index: number } | null = null;

  // Drag and drop state
  private draggingSlot: { container: 'player' | 'storage'; index: number } | null = null;
  private dragStartPos: Vector2 = new Vector2(0, 0);
  private currentMousePos: Vector2 = new Vector2(0, 0);
  private isDraggingItem: boolean = false;

  constructor() {
    super(0, 0, 280, 510); // Taller to fit all 20 player inventory slots
    this.isDraggable = true;
  }

  show(): void {
    super.show();
    this.title = t('panel_storage');
  }

  setPlayerInventory(inventory: InventoryComponent): void {
    this.playerInventory = inventory;
  }

  setStorageInventory(inventory: InventoryComponent | null): void {
    this.storageInventory = inventory;
  }

  protected onPanelClick(_pos: Vector2): boolean {
    if (!this.playerInventory || !this.storageInventory) return false;

    // If we're dragging, let handleMouseUp handle it
    if (this.isDraggingItem) {
      return true;
    }

    return false;
  }

  /** Handle mouse down - start potential drag */
  handleMouseDown(pos: Vector2): boolean {
    if (!this.isVisible || !this.containsPoint(pos)) return false;
    if (!this.playerInventory || !this.storageInventory) return false;

    const slot = this.getSlotAtPosition(pos);
    if (slot) {
      const inventory = slot.container === 'player' ? this.playerInventory : this.storageInventory;
      if (inventory?.getSlot(slot.index)) {
        this.draggingSlot = slot;
        this.dragStartPos = pos;
        this.currentMousePos = pos;
        this.isDraggingItem = false;
        return true;
      }
    }
    return false;
  }

  /** Handle mouse up - complete drag or click */
  handleMouseUp(pos: Vector2): boolean {
    if (!this.playerInventory || !this.storageInventory) {
      this.draggingSlot = null;
      this.isDraggingItem = false;
      return false;
    }

    if (this.draggingSlot && this.isDraggingItem) {
      const targetSlot = this.getSlotAtPosition(pos);

      if (targetSlot) {
        this.handleDrop(this.draggingSlot, targetSlot);
      }

      this.draggingSlot = null;
      this.isDraggingItem = false;
      return true;
    }

    // If we weren't dragging, it's a click - transfer item
    if (this.draggingSlot && !this.isDraggingItem) {
      this.handleSlotClick(this.draggingSlot.container, this.draggingSlot.index);
    }

    this.draggingSlot = null;
    this.isDraggingItem = false;
    return false;
  }

  /** Handle dropping an item from one slot to another */
  private handleDrop(source: { container: 'player' | 'storage'; index: number }, target: { container: 'player' | 'storage'; index: number }): void {
    if (!this.playerInventory || !this.storageInventory) return;

    const sourceInv = source.container === 'player' ? this.playerInventory : this.storageInventory;
    const targetInv = target.container === 'player' ? this.playerInventory : this.storageInventory;

    // Same container - swap slots
    if (source.container === target.container) {
      sourceInv.swapSlots(source.index, target.index);
    } else {
      // Different containers - transfer item
      const sourceSlot = sourceInv.getSlot(source.index);
      const targetSlot = targetInv.getSlot(target.index);

      if (sourceSlot) {
        if (targetSlot) {
          // Both slots have items - swap them
          const sourceItem = sourceSlot.item;
          const sourceQty = sourceSlot.quantity;
          const targetItem = targetSlot.item;
          const targetQty = targetSlot.quantity;

          // Remove both items
          sourceInv.removeItemAtSlot(source.index, sourceQty);
          targetInv.removeItemAtSlot(target.index, targetQty);

          // Add them to opposite slots (try to put in specific slot by adding to empty inventory first)
          sourceInv.addItem(targetItem, targetQty);
          targetInv.addItem(sourceItem, sourceQty);
        } else {
          // Target is empty - just transfer
          const overflow = targetInv.addItem(sourceSlot.item, sourceSlot.quantity);
          const transferred = sourceSlot.quantity - overflow;
          if (transferred > 0) {
            sourceInv.removeItemAtSlot(source.index, transferred);
          }
        }
      }
    }
  }

  handleMouseMove(pos: Vector2): boolean {
    if (super.handleMouseMove(pos)) return true;

    this.currentMousePos = pos;

    // Check if we should start dragging
    if (this.draggingSlot && !this.isDraggingItem) {
      const dragThreshold = 5;
      const dist = Math.sqrt(
        Math.pow(pos.x - this.dragStartPos.x, 2) +
        Math.pow(pos.y - this.dragStartPos.y, 2)
      );
      if (dist > dragThreshold) {
        this.isDraggingItem = true;
      }
    }

    if (this.isVisible && this.containsPoint(pos)) {
      this.hoveredSlot = this.getSlotAtPosition(pos);
      return true;
    } else {
      this.hoveredSlot = null;
    }
    return this.isDraggingItem;
  }

  /** Check if currently dragging an item */
  isItemDragging(): boolean {
    return this.isDraggingItem;
  }

  private getSlotAtPosition(pos: Vector2): { container: 'player' | 'storage'; index: number } | null {
    // Storage section (top) - 20 slots = 4 rows
    const storageStartX = this.bounds.x + 20;
    const storageStartY = this.bounds.y + this.titleBarHeight + 30;

    // Player inventory section (bottom) - after 4 rows of storage + divider
    const playerStartX = this.bounds.x + 20;
    const playerStartY = this.bounds.y + this.titleBarHeight + 225;

    // Check storage slots
    if (this.storageInventory) {
      const storageSlots = this.storageInventory.getCapacity();
      for (let i = 0; i < storageSlots; i++) {
        const col = i % this.slotsPerRow;
        const row = Math.floor(i / this.slotsPerRow);
        const x = storageStartX + col * (this.slotSize + 5);
        const y = storageStartY + row * (this.slotSize + 5);

        if (pos.x >= x && pos.x <= x + this.slotSize &&
            pos.y >= y && pos.y <= y + this.slotSize) {
          return { container: 'storage', index: i };
        }
      }
    }

    // Check all 20 player inventory slots
    if (this.playerInventory) {
      const playerSlots = this.playerInventory.getCapacity();
      for (let i = 0; i < playerSlots; i++) {
        const col = i % this.slotsPerRow;
        const row = Math.floor(i / this.slotsPerRow);
        const x = playerStartX + col * (this.slotSize + 5);
        const y = playerStartY + row * (this.slotSize + 5);

        if (pos.x >= x && pos.x <= x + this.slotSize &&
            pos.y >= y && pos.y <= y + this.slotSize) {
          return { container: 'player', index: i };
        }
      }
    }

    return null;
  }

  private handleSlotClick(container: 'player' | 'storage', index: number): void {
    if (!this.playerInventory || !this.storageInventory) return;

    const sourceInv = container === 'player' ? this.playerInventory : this.storageInventory;
    const targetInv = container === 'player' ? this.storageInventory : this.playerInventory;

    const slot = sourceInv.getSlot(index);
    if (slot) {
      // Transfer the entire stack
      // addItem returns overflow (amount that couldn't be added)
      const overflow = targetInv.addItem(slot.item, slot.quantity);
      const transferred = slot.quantity - overflow;

      if (transferred > 0) {
        // Remove from source at specific slot
        sourceInv.removeItemAtSlot(index, transferred);
      }
    }
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    if (!this.playerInventory || !this.storageInventory) {
      ctx.fillStyle = '#888';
      ctx.font = '14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('No storage selected', this.bounds.x + this.bounds.width / 2, this.bounds.y + 150);
      return;
    }

    // Storage section label
    ctx.fillStyle = '#4ecca3';
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(t('storage_chest'), this.bounds.x + 20, this.bounds.y + this.titleBarHeight + 20);

    // Render storage slots
    const storageStartX = this.bounds.x + 20;
    const storageStartY = this.bounds.y + this.titleBarHeight + 30;
    this.renderSlots(ctx, this.storageInventory, storageStartX, storageStartY, 'storage');

    // Divider - after 4 rows of storage (4 rows * 45 pixels each = 180 + 30 start = 210)
    ctx.strokeStyle = '#555';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.bounds.x + 20, this.bounds.y + this.titleBarHeight + 205);
    ctx.lineTo(this.bounds.x + this.bounds.width - 20, this.bounds.y + this.titleBarHeight + 205);
    ctx.stroke();

    // Player inventory section label
    ctx.fillStyle = '#4ecca3';
    ctx.font = 'bold 12px monospace';
    ctx.fillText(t('storage_your_inventory'), this.bounds.x + 20, this.bounds.y + this.titleBarHeight + 218);

    // Render all player inventory slots (20 slots = 4 rows)
    const playerStartX = this.bounds.x + 20;
    const playerStartY = this.bounds.y + this.titleBarHeight + 225;
    this.renderSlots(ctx, this.playerInventory, playerStartX, playerStartY, 'player');

    // Render dragged item at cursor
    if (this.isDraggingItem && this.draggingSlot) {
      const draggedInv = this.draggingSlot.container === 'player' ? this.playerInventory : this.storageInventory;
      const draggedItem = draggedInv?.getSlot(this.draggingSlot.index);
      if (draggedItem) {
        const dragX = this.currentMousePos.x - this.slotSize / 2;
        const dragY = this.currentMousePos.y - this.slotSize / 2;

        // Shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(dragX + 4, dragY + 4, this.slotSize, this.slotSize);

        // Item background
        ctx.fillStyle = 'rgba(30, 30, 50, 0.95)';
        ctx.fillRect(dragX, dragY, this.slotSize, this.slotSize);
        ctx.strokeStyle = '#4ecca3';
        ctx.lineWidth = 2;
        ctx.strokeRect(dragX, dragY, this.slotSize, this.slotSize);

        this.renderItem(ctx, dragX, dragY, draggedItem);
      }
    }

    // Instructions
    ctx.fillStyle = '#888';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(t('storage_click_transfer'), this.bounds.x + this.bounds.width / 2, this.bounds.y + this.bounds.height - 10);

    // Render tooltip for hovered item (when not dragging)
    if (!this.isDraggingItem && this.hoveredSlot) {
      const inventory = this.hoveredSlot.container === 'player' ? this.playerInventory : this.storageInventory;
      const slot = inventory?.getSlot(this.hoveredSlot.index);
      if (slot) {
        this.renderTooltip(ctx, slot);
      }
    }
  }

  private renderTooltip(ctx: CanvasRenderingContext2D, slot: ItemStack): void {
    const tooltipX = this.bounds.x + this.bounds.width + 10;
    const tooltipY = this.bounds.y + this.titleBarHeight;
    const tooltipWidth = 180;
    const tooltipHeight = 80;

    // Background
    ctx.fillStyle = 'rgba(20, 20, 40, 0.95)';
    ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    ctx.strokeStyle = '#4ecca3';
    ctx.lineWidth = 1;
    ctx.strokeRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    // Get localized item info
    const itemInfo = Localization.getItem(slot.item.id);
    const itemName = itemInfo?.name || slot.item.name;
    const itemDesc = itemInfo?.description || slot.item.description;

    // Item name
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(itemName, tooltipX + 10, tooltipY + 20);

    // Item type
    ctx.fillStyle = slot.item.getColor();
    ctx.font = '11px monospace';
    ctx.fillText(slot.item.type, tooltipX + 10, tooltipY + 35);

    // Description
    ctx.fillStyle = '#aaa';
    ctx.font = '11px monospace';
    const maxChars = 25;
    if (itemDesc.length > maxChars) {
      ctx.fillText(itemDesc.substring(0, maxChars) + '...', tooltipX + 10, tooltipY + 55);
    } else {
      ctx.fillText(itemDesc, tooltipX + 10, tooltipY + 55);
    }

    // Value
    ctx.fillStyle = '#f1c40f';
    ctx.fillText(`${t('tooltip_value')}: ${slot.item.value}`, tooltipX + 10, tooltipY + 70);
  }

  private renderSlots(
    ctx: CanvasRenderingContext2D,
    inventory: InventoryComponent,
    startX: number,
    startY: number,
    container: 'player' | 'storage',
    maxSlots?: number
  ): void {
    const slots = maxSlots ?? inventory.getCapacity();
    const hoverSlot = this.isDraggingItem ? this.getSlotAtPosition(this.currentMousePos) : null;

    for (let i = 0; i < slots; i++) {
      const col = i % this.slotsPerRow;
      const row = Math.floor(i / this.slotsPerRow);
      const x = startX + col * (this.slotSize + 5);
      const y = startY + row * (this.slotSize + 5);

      const isHovered = this.hoveredSlot?.container === container && this.hoveredSlot?.index === i;
      const isDropTarget = hoverSlot?.container === container && hoverSlot?.index === i && this.isDraggingItem;
      const isBeingDragged = this.draggingSlot?.container === container && this.draggingSlot?.index === i && this.isDraggingItem;
      const slot = inventory.getSlot(i);

      // Slot background
      if (isDropTarget) {
        ctx.fillStyle = 'rgba(78, 204, 163, 0.7)';
      } else if (isHovered && !this.isDraggingItem) {
        ctx.fillStyle = 'rgba(78, 204, 163, 0.3)';
      } else {
        ctx.fillStyle = 'rgba(50, 50, 70, 0.8)';
      }
      ctx.fillRect(x, y, this.slotSize, this.slotSize);

      // Border
      if (isDropTarget) {
        ctx.strokeStyle = '#7dffcc';
        ctx.lineWidth = 3;
      } else if (isHovered && !this.isDraggingItem) {
        ctx.strokeStyle = '#4ecca3';
        ctx.lineWidth = 2;
      } else {
        ctx.strokeStyle = '#555';
        ctx.lineWidth = 1;
      }
      ctx.strokeRect(x, y, this.slotSize, this.slotSize);

      // Item (dimmed if being dragged)
      if (slot && !isBeingDragged) {
        this.renderItem(ctx, x, y, slot);
      } else if (slot && isBeingDragged) {
        ctx.globalAlpha = 0.3;
        this.renderItem(ctx, x, y, slot);
        ctx.globalAlpha = 1.0;
      }
    }
  }

  private renderItem(ctx: CanvasRenderingContext2D, x: number, y: number, slot: ItemStack): void {
    const iconSize = this.slotSize - 10;
    const iconX = x + 5;
    const iconY = y + 5;

    // Try to render pixel art icon first
    if (hasItemIcon(slot.item.id)) {
      renderItemIcon(ctx, slot.item.id, iconX, iconY, iconSize);
    } else {
      // Fallback to colored square with letter
      ctx.fillStyle = slot.item.getColor();
      ctx.fillRect(iconX, iconY, iconSize, iconSize);

      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(iconX, iconY, iconSize, iconSize);

      // Item initial
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(slot.item.name.charAt(0), x + this.slotSize / 2, y + this.slotSize / 2 + 4);
    }

    // Quantity with background for better visibility
    if (slot.quantity > 1) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(x + this.slotSize - 18, y + this.slotSize - 14, 16, 12);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(`${slot.quantity}`, x + this.slotSize - 3, y + this.slotSize - 3);
    }
  }
}
